/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheckCircle,
  faCheckSquare,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { selectSessionInformation } from '../../../redux/slices/sessionSlice';
import formatTimestamp from '../../../utility/formatTimestamp';

const formatUptime = (uptimeInSeconds) => {
  if (!uptimeInSeconds) return '0';
  if (Number.isNaN(Number(uptimeInSeconds))) {
    // some devices return already formatted string that contains non-numerical digits
    // in that case we just return the string
    return uptimeInSeconds;
  }
  const seconds = Math.floor(Number(uptimeInSeconds));
  // Calculate time components
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  // Format hours, minutes, seconds with leading zeros
  const timeString = [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    remainingSeconds.toString().padStart(2, '0'),
  ].join(':');
  return days > 0 ? `${days} days ${timeString}` : timeString;
};

function DeviceDetailsTable({
  deviceInfo, deviceImage, latestFirmware,
}) {
  const { timezone } = useSelector(selectSessionInformation);
  const lastMessageTimestamp = deviceInfo.lastMessageTimestamp
    ? deviceInfo.lastMessageTimestamp.epochSecond
    : 0;

  const handleDeviceInterfaceClick = (ip, isHttps, port) => {
    const protocol = isHttps ? 'https' : 'http';
    const portString = port ? `:${port}` : '';
    const newWindow = window.open();
    if (newWindow) {
      newWindow.opener = null;
      newWindow.location = `${protocol}://${ip}${portString}`;
    }
  };

  const compareFirmwareVersions = (version1, version2) => {
    if (!version1 || !version2) {
      return true;
    }
    const cleanedVersion1 = version1.replace(/[^0-9.]/g, '');
    const cleanedVersion2 = version2.replace(/[^0-9.]/g, '');

    return cleanedVersion1 === cleanedVersion2;
  };

  const calculateNextPushTime = (lastMessageTimestampSeconds, pushIntervalSeconds) => {
    if (!lastMessageTimestampSeconds || !pushIntervalSeconds) return '';
    const nextPushTime = moment.unix(lastMessageTimestampSeconds).tz(timezone)
      .add(pushIntervalSeconds, 'seconds');
    return formatTimestamp(nextPushTime.unix(), timezone, 'MMMM D, YYYY, h:mm A');
  };

  return (
    <div className="px-4">
      <div className="row">
        {/* First Column */}
        <div className="col-12 col-md-6 col-xl-4">
          <dl className="row mb-0">
            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Uptime</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">{formatUptime(deviceInfo.uptime)}</dd>

            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Push Status</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">{deviceInfo.pushStatus === 1 ? 'Pushing' : 'Not Pushing'}</dd>

            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Link Status</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-truncate">
              {deviceInfo.isLinkConnected ? (
                <span>
                  Connected to
                  {' '}
                  <a href={`/links/${deviceInfo.linkId}`}>
                    {deviceInfo.linkName}
                  </a>
                </span>
              ) : (
                'Not connected to a Link'
              )}
            </dd>

            {deviceInfo.ethIp && (
            <>
              <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Ethernet IP</dt>
              <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">
                {deviceInfo.ethIp}
                {' '}
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="text-primary"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleDeviceInterfaceClick(
                    deviceInfo.ethIp,
                    deviceInfo.isHttps,
                    deviceInfo.httpPort,
                  )}
                  title="Open device interface"
                  aria-label="Open device interface"
                />
              </dd>
            </>
            )}

            {deviceInfo.wlanIp && (
            <>
              <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Wi-Fi IP</dt>
              <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">
                {deviceInfo.wlanIp}
                {' '}
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="text-primary"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleDeviceInterfaceClick(
                    deviceInfo.wlanIp,
                    deviceInfo.isHttps,
                    deviceInfo.httpPort,
                  )}
                  title="Open device interface"
                  aria-label="Open device interface"
                />
              </dd>
            </>
            )}
          </dl>
        </div>

        {/* Second Column */}
        <div className="col-12 col-md-6 col-xl-4">
          <dl className="row mb-0">
            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">MAC Address</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">{deviceInfo.macAddress}</dd>

            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">
              Serial Number
            </dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">{deviceInfo.serialnumber}</dd>

            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Firmware</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">
              {deviceInfo.firmware}
              {' '}
              {compareFirmwareVersions(deviceInfo.firmware, latestFirmware) ? (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-success"
                  title="Firmware is up to date"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="text-warning"
                  title="Firmware update available"
                />
              )}
            </dd>

            <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Model</dt>
            <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">{deviceInfo.type}</dd>

            {(deviceInfo.warrantyEndDate
                && moment().unix() < parseInt(deviceInfo.warrantyEndDate.epochSecond, 10)) && (
                <>
                  <dt className="col-5 col-sm-5 col-md-4 text-muted text-nowrap">Warranty</dt>
                  <dd className="col-7 col-sm-7 col-md-8 mb-0 text-nowrap text-truncate">
                    <FontAwesomeIcon icon={faCheckSquare} className="text-success" title="Device under warranty" />
                  </dd>
                </>
            )}
          </dl>
        </div>

        {/* Image Column */}
        <div className="col-12 col-md-6 col-xl-4 d-flex justify-content-center align-items-start">
          <div className="img-container">
            <img
              src={deviceImage}
              alt="Device"
              className="img-fluid"
              style={{ maxHeight: '250px' }}
            />
          </div>
        </div>
        <div className="w-100" />

        <div className="row mt-3 p-3">
          <div className="col-12">
            <p className="text-muted">
              Last push received
              {' '}
              <b>
                {lastMessageTimestamp ? formatTimestamp(lastMessageTimestamp, timezone, 'MMMM D, YYYY, h:mm A') : ''}
              </b>
              {' '}
              Next push expected by
              {' '}
              {/* eslint-disable-next-line max-len */}
              <b>{lastMessageTimestamp ? calculateNextPushTime(lastMessageTimestamp, deviceInfo.pushInterval) : ''}</b>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

DeviceDetailsTable.propTypes = {
  deviceInfo: PropTypes.shape({
    uptime: PropTypes.string,
    pushStatus: PropTypes.number,
    isLinkConnected: PropTypes.bool,
    linkName: PropTypes.string,
    linkId: PropTypes.number,
    ethIp: PropTypes.string,
    wlanIp: PropTypes.string,
    isHttps: PropTypes.bool,
    httpPort: PropTypes.number,
    macAddress: PropTypes.string,
    serialnumber: PropTypes.string,
    firmware: PropTypes.string,
    type: PropTypes.string,
    warrantyEndDate: PropTypes.string,
    pushInterval: PropTypes.number,
    // eslint-disable-next-line max-len
    lastMessageTimestamp: PropTypes.shape({ epochSecond: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }),
    assignmentStartDate: PropTypes.shape({ epochSecond: PropTypes.string }),
  }).isRequired,
  deviceImage: PropTypes.string.isRequired,
  latestFirmware: PropTypes.string.isRequired,
};

export default DeviceDetailsTable;
